import { useCallback, useEffect, useMemo, useState } from 'react'
import { Col, Container, Row } from 'react-bootstrap'
import { createPortal } from 'react-dom'
import { FormattedMessage, useIntl } from 'react-intl'
import { useParams } from 'react-router-dom'
import TextareaAutosize from 'react-textarea-autosize'
import { useLazyQuery, useQuery } from '@apollo/client'
import { faWarning } from '@fortawesome/pro-regular-svg-icons'
import {
  faPenToSquare,
  faPrint,
  faSpinner,
} from '@fortawesome/pro-solid-svg-icons'
import { clsx } from 'clsx'
import { observer } from 'mobx-react'

import { showErrorModal } from 'Modules/order/utils/errorModal.utils'
import {
  getExternalIdCustomName,
  getOrderAttribute,
} from 'Modules/order/utils/order.utils'

import {
  Alert,
  Badge,
  Checkbox,
  Heading,
  LinkButton,
  PrimaryButton,
  TertiaryButton,
  Textarea,
} from 'Components/_theme'
import { FormattedCurrency } from 'Components/FormattedCurrency/FormattedCurrency'
import { Icon } from 'Components/Icon/Icon'
import { ImageWithFallback } from 'Components/ImageWithFallback/ImageWithFallback'

import { useStore } from 'Stores/index'

import { useActiveUser, useIsOrganisation, useModal } from 'Hooks'

import { countryISOCodes } from 'Constants/constants'
import { E2E_NEW_ORDER_SUBMIT } from 'Constants/e2e'
import type {
  getOrderTerms,
  getOrderTermsVariables,
} from 'Constants/graphql/queries/detail/__generated__/getOrderTerms'
import { GET_ORDER_TERMS } from 'Constants/graphql/queries/detail/GetOrderTerms'
import type {
  getEquipmentUsageAgreementHTMLTemplate,
  getEquipmentUsageAgreementHTMLTemplateVariables,
} from 'Constants/graphql/queries/feature/__generated__/getEquipmentUsageAgreementHTMLTemplate'
import { GET_AGREEMENT_HTML_TEMPLATE } from 'Constants/graphql/queries/feature/GetEquipmentUsageAgreementHTMLTemplate'
import { SupportedLanguagesEnum } from 'Constants/locale'

import { ImagePlaceholderTypesEnum } from 'Types/imagePlaceholders.type'

import { OrderDeviceResponseVariant } from 'Portal/src/mappers/orderDeviceMapper/orderDevice.types'
import { ShippingAddressTypeEnum } from 'Portal/src/types/address/address.types'

import { OrderStickyFooter } from '../OrderStickyFooter/OrderStickyFooter'
import { OrderSummaryDeviceRow } from './components/OrderSummaryDeviceRow'
import { OrderSummaryHeadingRow } from './components/OrderSummaryHeadingRow'
import { OrderSummaryQuantityInput } from './components/OrderSummaryQuantityInput'
import { OrderSummaryTextField } from './components/OrderSummaryTextField'
import { MAX_PURCHASE_ADDON_QUANTITY } from './OrderSummary.constants'
import type {
  OrderSummaryDeviceRowRendererProps,
  OrderSummaryProps,
  OrderSummaryTerms,
} from './OrderSummary.types'

export const OrderSummary = observer((props: OrderSummaryProps) => {
  const {
    chosenDevice,
    chosenAccessories,
    chosenShippingAddress,
    isLoading,
    onOrderButtonClick,
    totalPrice,
    onBackButtonClick,
    onChangeDeviceButtonClick,
    onSetChosenDeviceQuantity,
    onSetAccessoryTextField,
    onSetAccessoryQuantity,
    onSetAddonDeviceTextField,
    onSetChosenDeviceTextField,
    onSetExternalId,
    isEarlyUpgrade,
    earlyUpgradeFee,
    isNewOrder,
    orderDeviceResponseVariant,
    orderExternalId,
    onSetPhonePlanDetails,
    phonePlanDetails,
    orderType,
    showCopaymentInformation,
  } = props

  const {
    portalConfigStore,
    portalSettingsStore,
    orderStore,
    organisationStore,
    userStore,
  } = useStore()

  const { portalConfig } = portalConfigStore
  const {
    i18n: { language },
  } = portalSettingsStore
  const { isProductOffer, limitationRules } = orderStore
  const { organisationConfigList } = organisationStore
  const isOrganisation = useIsOrganisation()

  const { setModal, hideModal } = useModal()

  const { employeeId = '' } = useParams<{
    employeeId?: string
  }>()

  const isBundle = chosenDevice?.isBundle

  const chosenDeviceName = isBundle
    ? chosenDevice.bundleName || chosenDevice.customDeviceName
    : chosenDevice?.deviceDefinition.name

  const chosenDeviceImage = isBundle
    ? chosenDevice.bundleImage
    : chosenDevice?.deviceDefinition.imageURL

  const enableCustomerLabels = portalConfig?.enableCustomerLabels ?? false
  const enablePoRcnInOrderSummary =
    isOrganisation && !!portalConfig?.poRcnInOrderSummary
  const enableClientReferenceInOrderSummary =
    isOrganisation && !!portalConfig?.customClientReferenceInOrderSummary
  const enableBillingIdentifierInOrderSummary =
    isOrganisation && !!portalConfig?.billingIdentifierInOrderSummary
  const enableChosenDeviceQuantityLimitations =
    (isNewOrder &&
      isOrganisation &&
      !!employeeId &&
      !portalConfig?.allowAdminUsersToOrderNewDeviceForEmployeesWithoutLimitations) ||
    false

  const intl = useIntl()

  const { activeOrganisation, activeEmployee } = useActiveUser()
  const organisation =
    activeOrganisation || activeEmployee?.organisation || null

  const {
    data: orderTerms,
    loading: isTermsListLoading,
    error: termsError,
  } = useQuery<getOrderTerms, getOrderTermsVariables>(GET_ORDER_TERMS, {
    onError: error => {
      showErrorModal(
        () => {
          hideModal()
        },
        setModal,
        error,
        hideModal,
      )
    },
    variables: {
      orderTermsInput: {
        hasItemsWithCoPayment: [...chosenAccessories.values()].some(
          accessory => accessory.copaymentNet > 0,
        ),
        offerID: chosenDevice?.offerId || '0',
        organisationID: organisation?.id || '0',
      },
    },
  })

  const [termsAcceptance, setTermsAcceptance] = useState<OrderSummaryTerms>({
    orgTerms: false,
    productOfferTerms: false,
    rentCategoryTerms: false,
  })

  const termsForCurrentLanguage = orderTerms?.getOrderTerms.OrderTerms?.find(
    orderTerm => orderTerm?.locale === language,
  )?.OrderTerm

  const termsFallback = orderTerms?.getOrderTerms.OrderTerms?.find(
    orderTerm => orderTerm?.locale === SupportedLanguagesEnum.EN,
  )?.OrderTerm

  const applicableTerms = termsForCurrentLanguage ||
    termsFallback || {
      organisation: null,
      productOffer: null,
      rentCategory: null,
    }

  const terms = useMemo(
    () => [
      {
        checked: termsAcceptance.orgTerms,
        label: applicableTerms.organisation ?? '',
        name: 'orgTerms',
        show: !!applicableTerms.organisation,
      },
      {
        checked: termsAcceptance.rentCategoryTerms,
        label: applicableTerms.rentCategory ?? '',
        name: 'rentCategoryTerms',
        show: !!applicableTerms.rentCategory,
      },
      {
        checked: termsAcceptance.productOfferTerms,
        label: applicableTerms.productOffer ?? '',
        name: 'productOfferTerms',
        show: !!applicableTerms.productOffer,
      },
    ],
    [
      applicableTerms.organisation,
      applicableTerms.productOffer,
      applicableTerms.rentCategory,
      termsAcceptance.orgTerms,
      termsAcceptance.productOfferTerms,
      termsAcceptance.rentCategoryTerms,
    ],
  )

  const [
    getAgreementHtmlTemplate,
    {
      data: agreementHtmlTemplateData,
      loading: isAgreementHtmlTemplateDataLoading,
      errors: agreementHtmlTemplateErrors,
      error: agreementHtmlTemplateError,
      called: isAgreementHtmlTemplateCalled,
    },
  ] = useLazyQuery<
    getEquipmentUsageAgreementHTMLTemplate,
    getEquipmentUsageAgreementHTMLTemplateVariables
  >(GET_AGREEMENT_HTML_TEMPLATE, {
    errorPolicy: 'all',
    variables: {
      copaymentGross: chosenDevice?.copaymentGross ?? 0,
      copaymentNet: chosenDevice?.copaymentNet ?? 0,
      deviceDefinitionID: chosenDevice?.deviceDefinition?.id ?? '0',
      employeeID: employeeId || activeEmployee?.id?.toString() || '0',
      organizationID: organisation?.id?.toString() ?? '0',
      rentalDuration: (chosenDevice?.minimumContractDuration ?? 0).toString(),
      userID: userStore.user.userId?.toString() ?? '0',
    },
  })

  const handleShowAgreementTemplate = () => {
    const style = document.createElement('style')

    document.head.appendChild(style)

    style.textContent =
      '.print-mode{@media print{#root{display:none}#order-agreement-html-template{display:block}}}'

    document.body.classList.add('print-mode')

    window.onafterprint = () => {
      document.body.classList.remove('print-mode')
      document.head.removeChild(style)

      window.onafterprint = null
    }

    window.print()
  }

  useEffect(() => {
    if (
      portalConfig?.equipmentUsageAgreement &&
      organisation &&
      (employeeId || activeEmployee?.id?.toString())
    ) {
      getAgreementHtmlTemplate()
    }
  }, [
    activeEmployee?.id,
    employeeId,
    getAgreementHtmlTemplate,
    organisation,
    portalConfig?.equipmentUsageAgreement,
  ])

  const isDevicePhoneRelatedType = (type?: string | null) =>
    type === 'sim_card' || type === 'phone_plan'

  const showPhonePlanDetailsField = useMemo(
    () =>
      isProductOffer &&
      (isDevicePhoneRelatedType(chosenDevice?.deviceDefinition.productType) ||
        chosenDevice?.addonDevices?.some(device =>
          isDevicePhoneRelatedType(device.deviceDefinition.productType),
        ) ||
        [...chosenAccessories.values()].some(accessory =>
          isDevicePhoneRelatedType(accessory.deviceDefinition.productType),
        )),
    [chosenAccessories, chosenDevice, isProductOffer],
  )

  const atLeastOneTermIsNotAccepted =
    (!!applicableTerms.organisation && !termsAcceptance.orgTerms) ||
    (!!applicableTerms.rentCategory && !termsAcceptance.rentCategoryTerms) ||
    (!!applicableTerms.productOffer && !termsAcceptance.productOfferTerms)

  const showNestedView =
    chosenDevice?.bundleImage !== chosenDevice?.deviceDefinition.imageURL ||
    (chosenDevice?.bundleName || chosenDevice?.customDeviceName) !==
      chosenDevice?.deviceDefinition.name

  const organisationConfigAttributeForThisOrder = getOrderAttribute(
    organisationConfigList,
    orderType,
  )

  const isExternalIdEnabledForThisType =
    organisationConfigAttributeForThisOrder &&
    organisationConfigAttributeForThisOrder.attributeValue !== 'disabled'

  const isExternalIdFieldMandatory =
    organisationConfigAttributeForThisOrder?.attributeValue === 'mandatory'

  const externalIdCustomName = getExternalIdCustomName(
    organisationConfigList,
    intl,
  )

  const isRentCategoryDevice =
    orderDeviceResponseVariant === OrderDeviceResponseVariant.RENT_CATEGORY

  const renderSummaryHeadingRow = useMemo(
    () => (
      <OrderSummaryHeadingRow
        isNewOrder={isNewOrder}
        isOrganisation={isOrganisation}
      />
    ),
    [isNewOrder, isOrganisation],
  )

  const renderSummaryDeviceRow = useCallback(
    (props: OrderSummaryDeviceRowRendererProps) => (
      <OrderSummaryDeviceRow
        key={props.deviceId ?? props.deviceName}
        {...{
          ...props,
          enableBillingIdentifierInOrderSummary,
          enableChosenDeviceQuantityLimitations,
          enableClientReferenceInOrderSummary,
          enableCustomerLabels,
          enablePoRcnInOrderSummary,
          isNewOrder,
          isOrganisation,
          limitationRules,
          onSetAddonDeviceTextField,
          onSetChosenDeviceQuantity,
          onSetChosenDeviceTextField,
        }}
      />
    ),
    [
      enableBillingIdentifierInOrderSummary,
      enableClientReferenceInOrderSummary,
      limitationRules,
      enableCustomerLabels,
      enablePoRcnInOrderSummary,
      enableChosenDeviceQuantityLimitations,
      isNewOrder,
      isOrganisation,
      onSetAddonDeviceTextField,
      onSetChosenDeviceQuantity,
      onSetChosenDeviceTextField,
    ],
  )

  const maxAccessoriesQuantity = chosenDevice?.quantity
  const isAnyDevicePurchaseType =
    chosenDevice?.isPurchase ||
    chosenDevice?.addonDevices?.some(device => device.isPurchase) ||
    [...chosenAccessories.values()].some(accessory => accessory.isPurchase)

  const showEUAError =
    portalConfig?.equipmentUsageAgreement &&
    organisation &&
    (employeeId || activeEmployee?.id?.toString()) &&
    (agreementHtmlTemplateErrors ||
      agreementHtmlTemplateError ||
      (isAgreementHtmlTemplateCalled &&
        !isAgreementHtmlTemplateDataLoading &&
        !agreementHtmlTemplateData?.getEquipmentUsageAgreementHTMLTemplate))

  return (
    <>
      <Container className="OrderFlow-summary" fluid>
        <Row className="overflow-auto">
          <Col className="scrollable-on-mobile">
            {renderSummaryHeadingRow}

            {(showNestedView || !isBundle) &&
              renderSummaryDeviceRow({
                billingIdentifier: chosenDevice?.billingIdentifier || '',
                customClientReference:
                  chosenDevice?.customClientReference || '',
                deviceCustomerLabel: chosenDevice?.customerLabel || '',
                deviceImage: chosenDeviceImage || '',
                deviceName: chosenDeviceName || '',
                devicePrice: chosenDevice?.copaymentNet || 0,
                deviceQuantity: chosenDevice?.quantity || 1,
                deviceRentalDuration:
                  chosenDevice?.minimumContractDuration || 0,
                deviceType: chosenDevice?.deviceDefinition.productType,
                isBundleRow: isBundle,
                isPurchase: chosenDevice?.isPurchase,
                poRcn: chosenDevice?.poRcn || '',
              })}

            {isBundle && (
              <>
                {renderSummaryDeviceRow({
                  billingIdentifier: chosenDevice?.billingIdentifier || '',
                  customClientReference:
                    chosenDevice?.customClientReference || '',
                  deviceCustomerLabel: chosenDevice?.customerLabel || '',
                  deviceImage: chosenDevice?.deviceDefinition.imageURL || '',
                  deviceName:
                    chosenDevice?.customDeviceName ||
                    chosenDevice?.deviceDefinition.name ||
                    '',
                  devicePrice: chosenDevice?.copaymentNet || 0,
                  deviceQuantity: chosenDevice?.quantity || 1,
                  deviceRentalDuration:
                    chosenDevice?.minimumContractDuration || 0,
                  deviceType: chosenDevice?.deviceDefinition.productType,
                  isBundleItemRow: showNestedView,
                  isPurchase: chosenDevice?.isPurchase,
                  poRcn: chosenDevice?.poRcn || '',
                })}

                {chosenDevice?.addonDevices?.map(addonDevice =>
                  renderSummaryDeviceRow({
                    billingIdentifier: addonDevice?.billingIdentifier || '',
                    customClientReference:
                      addonDevice?.customClientReference || '',
                    deviceCustomerLabel: addonDevice?.customerLabel || '',
                    deviceId: addonDevice.id,
                    deviceImage: addonDevice.deviceDefinition.imageURL || '',
                    deviceName:
                      addonDevice.customDeviceName ||
                      addonDevice.deviceDefinition.name,
                    devicePrice: addonDevice.copaymentNet,
                    deviceQuantity: addonDevice.quantity,
                    deviceRentalDuration:
                      addonDevice.minimumContractDuration || 0,
                    deviceType: addonDevice.deviceDefinition.productType,
                    isAddon: true,
                    isBundleItemRow: showNestedView,
                    isCreateSeparateRental: addonDevice.isCreateSeparateRental,
                    isPurchase: addonDevice.isPurchase,
                    poRcn: addonDevice?.poRcn || '',
                  }),
                )}
              </>
            )}

            {[...chosenAccessories].map(chosenAccessory => {
              const [_id, accessory] = chosenAccessory

              return (
                <Row
                  key={accessory.rentCategoryId + accessory.deviceDefinition.id}
                  className={clsx(
                    'd-flex px-4 py-6 device-overview',
                    isNewOrder && !isOrganisation && 'justify-content-between',
                  )}
                >
                  <Col className="ps-0" xs={isNewOrder ? 6 : 8}>
                    <div className="d-flex align-items-center">
                      <ImageWithFallback
                        alt={
                          accessory.customDeviceName ||
                          accessory.deviceDefinition.name
                        }
                        placeholderType={ImagePlaceholderTypesEnum.newOrderFlow}
                        src={accessory.deviceDefinition.imageURL || ''}
                      />

                      <div className="ms-2 ms-md-6">
                        <strong>
                          {accessory.customDeviceName ||
                            accessory.deviceDefinition.name}
                        </strong>

                        <br />

                        {accessory.isPurchase && (
                          <Badge
                            className="mt-4"
                            color="yellow"
                            rounded
                            variant="secondary"
                          >
                            <FormattedMessage id="NewOrder_purchase" />
                          </Badge>
                        )}
                      </div>
                    </div>
                  </Col>

                  <Col
                    className="d-flex justify-content-end align-items-center"
                    xs={2}
                  >
                    {accessory.isPurchase
                      ? '-'
                      : (isRentCategoryDevice ||
                          accessory.isCreateSeparateRental) && (
                          <>
                            {accessory?.minimumContractDuration}&nbsp;
                            <FormattedMessage id="NewOrderSummary_months" />
                          </>
                        )}
                  </Col>

                  {isNewOrder && isOrganisation && (
                    <Col
                      className="d-flex justify-content-end align-items-center"
                      xs={2}
                    >
                      <OrderSummaryQuantityInput
                        canChooseOnlyMinOrMaxQuantity={
                          !accessory.isPurchase &&
                          !accessory.isCreateSeparateRental &&
                          accessory.basisAmount + accessory.copaymentNet > 0
                        }
                        controlId="accessoryQuantity"
                        defaultValue={accessory.quantity ?? 1}
                        maxQuantity={
                          accessory.isPurchase
                            ? MAX_PURCHASE_ADDON_QUANTITY
                            : maxAccessoriesQuantity
                        }
                        minQuantity={0}
                        onChangeInput={value => {
                          onSetAccessoryQuantity(value, accessory)
                        }}
                      />
                    </Col>
                  )}

                  {(accessory.isPurchase ||
                    isRentCategoryDevice ||
                    accessory.isCreateSeparateRental) && (
                    <Col
                      className="d-flex justify-content-end align-items-center"
                      xs={2}
                    >
                      <FormattedCurrency value={accessory.copaymentNet || 0} />
                    </Col>
                  )}

                  {isOrganisation && enableCustomerLabels && (
                    <Col className="ps-0" md={8} xs={12}>
                      <OrderSummaryTextField
                        controlId="customAccessoryLabel"
                        defaultValue={accessory.customerLabel ?? ''}
                        onChangeLabel={event =>
                          onSetAccessoryTextField(
                            'customerLabel',
                            event.currentTarget.value,
                            accessory,
                          )
                        }
                        placeholderTranslationId="NewOrderSummary_label_placeholder"
                      />
                    </Col>
                  )}

                  {enablePoRcnInOrderSummary && (
                    <Col className="ps-0" md={8} xs={12}>
                      <OrderSummaryTextField
                        controlId="poRcnAccessory"
                        defaultValue={accessory.poRcn || ''}
                        onChangeLabel={event =>
                          onSetAccessoryTextField(
                            'poRcn',
                            event.currentTarget.value,
                            accessory,
                          )
                        }
                        placeholderTranslationId="NewOrderSummary_po_rcn_placeholder"
                      />
                    </Col>
                  )}

                  {enableClientReferenceInOrderSummary && (
                    <Col className="ps-0" md={8} xs={12}>
                      <OrderSummaryTextField
                        controlId="customClientReferenceAccessory"
                        defaultValue={accessory.customClientReference || ''}
                        onChangeLabel={event =>
                          onSetAccessoryTextField(
                            'customClientReference',
                            event.currentTarget.value,
                            accessory,
                          )
                        }
                        placeholderTranslationId="NewOrderSummary_custom_client_reference_placeholder"
                      />
                    </Col>
                  )}

                  {enableBillingIdentifierInOrderSummary && (
                    <Col className="ps-0" md={8} xs={12}>
                      <OrderSummaryTextField
                        controlId="billingIdentifierAccessory"
                        defaultValue={accessory.billingIdentifier || ''}
                        onChangeLabel={event =>
                          onSetAccessoryTextField(
                            'billingIdentifier',
                            event.currentTarget.value,
                            accessory,
                          )
                        }
                        placeholderTranslationId="NewOrderSummary_billing_identifier_placeholder"
                      />
                    </Col>
                  )}
                </Row>
              )
            })}
          </Col>

          {showPhonePlanDetailsField && (
            <Textarea
              aria-label={intl.formatMessage({
                id: 'NewOrderSummary_phone_plan_details',
              })}
              charactersCount={phonePlanDetails?.length || 0}
              className="p-0 mb-4"
              customComponent={TextareaAutosize}
              defaultValue={phonePlanDetails || ''}
              id="phonePlanDetails"
              maxLength={200}
              onChange={event =>
                onSetPhonePlanDetails(event.currentTarget.value)
              }
              placeholder={intl.formatMessage({
                id: 'NewOrderSummary_phone_plan_details',
              })}
              showCounter
            />
          )}
        </Row>

        <Row
          className={clsx({
            'pb-4': isEarlyUpgrade,
            'pb-6': !isEarlyUpgrade,
          })}
        >
          <Col>
            <Row className="price-container px-4 d-flex justify-content-between align-items-center">
              <Col className="ps-0" md="auto" xs={8}>
                <Heading as={5} size={4}>
                  <FormattedMessage id="NewOrderSummary_total" />
                </Heading>
              </Col>

              <Col className="text-end pe-0">
                <Heading as={5} className="mt-1" size={4}>
                  <FormattedCurrency value={totalPrice.totalWithoutTax} />
                </Heading>
              </Col>
            </Row>

            {portalConfig?.showCopaymentWithAndWithoutTax && (
              <Row className="price-container px-4 d-flex justify-content-between align-items-center">
                <Col className="ps-0" md="auto" xs={8}>
                  <Heading as={5} size={4}>
                    <FormattedMessage id="NewOrderSummary_total_gross" />
                  </Heading>
                </Col>

                <Col className="text-end pe-0">
                  <Heading as={5} className="mt-1" size={4}>
                    <FormattedCurrency value={totalPrice.totalWithTax} />
                  </Heading>
                </Col>
              </Row>
            )}

            {isOrganisation &&
              portalConfig?.showRentalBasisAmountAtOrderFlow &&
              !!totalPrice.totalBasis &&
              !chosenDevice?.forcePriceHide && (
                <Row className="price-container px-4 d-flex justify-content-between align-items-center">
                  <Col className="ps-0" md="auto" xs={8}>
                    <Heading as={5} size={4}>
                      <FormattedMessage id="NewOrderSummary_total_basis" />
                    </Heading>
                  </Col>

                  <Col className="text-end pe-0">
                    <Heading as={5} className="mt-1" size={4}>
                      <FormattedCurrency value={totalPrice.totalBasis} />
                    </Heading>
                  </Col>
                </Row>
              )}

            {!!chosenDevice?.calculatedNet && (
              <Row className="position-relative order-container green-container mt-4 px-4 d-flex justify-content-between align-items-center">
                <Col
                  className="d-flex align-items-center ps-0"
                  md="auto"
                  xs={8}
                >
                  <LinkButton
                    aria-label={intl.formatMessage({
                      id: 'Edit',
                    })}
                    className="text-secondary me-1 p-0"
                    onClick={onChangeDeviceButtonClick}
                  >
                    <Icon icon={faPenToSquare} size={14} />
                  </LinkButton>

                  <Heading as={5} size={4}>
                    &nbsp;
                    <FormattedMessage id="NewOrderSummary_net_total" />
                  </Heading>
                </Col>

                <Col className="d-flex align-items-center justify-content-end pe-0">
                  <div>
                    <Heading as={5} className="mt-1" size={4}>
                      ≈&nbsp;
                      <FormattedCurrency
                        value={totalPrice.totalCalculatedNet}
                      />
                    </Heading>
                  </div>
                </Col>
              </Row>
            )}

            {isAnyDevicePurchaseType && (
              <>
                <Row className="price-container px-4 d-flex justify-content-between align-items-center">
                  <Col className="ps-0" md="auto" xs={8}>
                    <Heading as={5} size={4}>
                      <FormattedMessage id="NewOrder_total_one_time_copayment_amount" />
                    </Heading>
                  </Col>

                  <Col className="text-end pe-0">
                    <Heading as={5} className="mt-1" size={4}>
                      <FormattedCurrency
                        value={totalPrice.totalPurchaseCopaymentWithoutTax}
                      />
                    </Heading>
                  </Col>
                </Row>

                {portalConfig?.showCopaymentWithAndWithoutTax && (
                  <Row className="price-container px-4 d-flex justify-content-between align-items-center">
                    <Col className="ps-0" md="auto" xs={8}>
                      <Heading as={5} size={4}>
                        <FormattedMessage id="NewOrder_total_one_time_copayment_amount_gross" />
                      </Heading>
                    </Col>

                    <Col className="text-end pe-0">
                      <Heading as={5} className="mt-1" size={4}>
                        <FormattedCurrency
                          value={totalPrice.totalPurchaseCopaymentWithTax}
                        />
                      </Heading>
                    </Col>
                  </Row>
                )}
              </>
            )}

            {isOrganisation &&
              isAnyDevicePurchaseType &&
              !chosenDevice?.forcePriceHide && (
                <Row className="price-container px-4 d-flex justify-content-between align-items-center">
                  <Col className="ps-0" md="auto" xs={8}>
                    <Heading as={5} size={4}>
                      <FormattedMessage id="NewOrder_total_one_time_purchase_amount" />
                    </Heading>
                  </Col>

                  <Col className="text-end pe-0">
                    <Heading as={5} className="mt-1" size={4}>
                      <FormattedCurrency value={totalPrice.totalPurchase} />
                    </Heading>
                  </Col>
                </Row>
              )}
          </Col>
        </Row>

        {isEarlyUpgrade && (
          <Row className="mt-2 pb-6">
            <Col>
              <Row className="blue-container p-4 d-flex justify-content-between">
                <Col className="ps-0" md="auto" xs={8}>
                  <Heading as={5} className="m-0 mb-2" size={4}>
                    <FormattedMessage id="Upgrade_Fee_ExclVAT" />
                  </Heading>

                  {showCopaymentInformation && (
                    <p className="m-0">
                      <FormattedMessage id="Order_upgrade_fee_info" />
                    </p>
                  )}
                </Col>

                <Col className="d-flex align-items-center justify-content-end pe-0">
                  <Heading as={5} className="mt-1" size={4}>
                    <FormattedCurrency value={earlyUpgradeFee || 0} />
                  </Heading>
                </Col>
              </Row>
            </Col>
          </Row>
        )}

        {isExternalIdEnabledForThisType && (
          <Row className="pt-4">
            <Col className="ps-0">
              <Heading as={6} className="mb-2" size={5}>
                {externalIdCustomName}
              </Heading>
              <OrderSummaryTextField
                controlId="orderExternalId"
                defaultValue={orderExternalId || ''}
                isRequired={isExternalIdFieldMandatory}
                onChangeLabel={event => onSetExternalId(event.target.value)}
                placeholderTranslationId={
                  isExternalIdFieldMandatory
                    ? 'NewOrderSummary_external_id_placeholder'
                    : 'NewOrderSummary_external_id_placeholder_optional'
                }
                placeholderTranslationValues={{
                  customName: externalIdCustomName,
                }}
              />
            </Col>
          </Row>
        )}

        <Row className={isExternalIdEnabledForThisType ? 'pt-2' : 'pt-4'}>
          <Col className="p-0">
            {chosenShippingAddress?.addressType !==
              ShippingAddressTypeEnum.HANDOVER && (
              <>
                <div className="d-flex justify-content-between align-items-center mb-2">
                  <Heading as={6} size={5}>
                    <FormattedMessage id="NewOrderSummary_shipping_address" />
                  </Heading>

                  <TertiaryButton
                    aria-label={intl.formatMessage({ id: 'Edit_address' })}
                    asIcon={faPenToSquare}
                    onClick={onBackButtonClick}
                  />
                </div>

                <Textarea
                  aria-label={intl.formatMessage({
                    id: 'NewOrderSummary_shipping_address',
                  })}
                  className="mb-6"
                  cols={3}
                  id="ShippingAddress"
                  onChange={event => event.preventDefault()}
                  value={`${chosenShippingAddress?.name}${
                    chosenShippingAddress?.nameAddition
                      ? ` ${chosenShippingAddress.nameAddition}\n`
                      : '\n'
                  }${
                    chosenShippingAddress?.country?.value === countryISOCodes.US
                      ? chosenShippingAddress.usAddress
                      : `${chosenShippingAddress?.street} ${chosenShippingAddress?.streetNumber} ${chosenShippingAddress?.streetAddition}`.trim()
                  }\n${chosenShippingAddress?.zipcode} ${
                    chosenShippingAddress?.country?.value === countryISOCodes.US
                      ? `${chosenShippingAddress.city}, ${
                          chosenShippingAddress.state?.value ?? ''
                        }`.trim()
                      : chosenShippingAddress?.city
                  } ${chosenShippingAddress?.country?.value ?? ''}`}
                />
              </>
            )}

            {terms.map(
              ({ show, name, ...rest }) =>
                show && (
                  <Checkbox
                    key={name}
                    className="text-linebreak mt-6"
                    id={name}
                    name={name}
                    onChange={() =>
                      setTermsAcceptance(termsAcceptance => ({
                        ...termsAcceptance,
                        [name]:
                          !termsAcceptance[name as keyof OrderSummaryTerms],
                      }))
                    }
                    {...rest}
                  />
                ),
            )}

            {agreementHtmlTemplateData &&
              agreementHtmlTemplateData.getEquipmentUsageAgreementHTMLTemplate
                ?.html && (
                <>
                  <LinkButton
                    className="mt-3"
                    iconLeft={faPrint}
                    onClick={handleShowAgreementTemplate}
                    size="small"
                  >
                    <FormattedMessage id="Order_view_contract" />
                  </LinkButton>

                  {createPortal(
                    <div
                      dangerouslySetInnerHTML={{
                        __html:
                          agreementHtmlTemplateData
                            .getEquipmentUsageAgreementHTMLTemplate.html,
                      }}
                      id="order-agreement-html-template"
                    />,
                    document.body,
                  )}
                </>
              )}

            {showEUAError && (
              <Alert className="mt-4" iconLeft={faWarning} variant="danger">
                <FormattedMessage id="NewOrderSummary_missing_EUA" />
              </Alert>
            )}
          </Col>
        </Row>
      </Container>

      <OrderStickyFooter
        handlePreviousAction={onBackButtonClick}
        renderNextButtonElement={
          showEUAError
            ? undefined
            : () => (
                <PrimaryButton
                  aria-label={intl.formatMessage({
                    id: 'NewOrderSummary_order',
                  })}
                  className="ms-1"
                  data-e2e={E2E_NEW_ORDER_SUBMIT}
                  disabled={
                    atLeastOneTermIsNotAccepted ||
                    (isExternalIdEnabledForThisType &&
                      isExternalIdFieldMandatory &&
                      !orderExternalId) ||
                    isTermsListLoading ||
                    isAgreementHtmlTemplateDataLoading ||
                    isLoading ||
                    !!termsError
                  }
                  id="order"
                  onClick={onOrderButtonClick}
                >
                  {isLoading ? (
                    <Icon icon={faSpinner} spin />
                  ) : (
                    <FormattedMessage id="NewOrderSummary_order" />
                  )}
                </PrimaryButton>
              )
        }
      />
    </>
  )
})
